import { useId, type ReactNode, type SelectHTMLAttributes } from 'react';
import './Select.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  options: SelectOption[];
  /** Guidance shown under the field; announced with the control. */
  hint?: ReactNode;
  /** Error text. Presence switches the field into its error state. */
  error?: string;
  /** Hide the label visually but keep it for screen readers. */
  labelHidden?: boolean;
}

/**
 * Labelled select, built to match `Input` exactly.
 *
 * It exists because it was the one control in the product that did not: every
 * `<select>` was a bare native one sitting beside bordered, rounded, padded
 * text fields, so a form read as two things assembled rather than one thing
 * designed. A design system with a hole in it is a design system people work
 * around.
 *
 * The chevron is drawn here rather than left to the platform, for the same
 * reason — and `appearance: none` means the closed control is ours while the
 * open list stays the operating system's, which is what people expect on a
 * phone.
 */
export function Select({
  label,
  options,
  hint,
  error,
  labelHidden = false,
  className,
  ...rest
}: SelectProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={['sc-field', className ?? ''].filter(Boolean).join(' ')}>
      <label className={`sc-field__label${labelHidden ? ' sc-visually-hidden' : ''}`} htmlFor={id}>
        {label}
      </label>

      <div className={`sc-field__control sc-select${error ? ' sc-field__control--error' : ''}`}>
        <select
          {...rest}
          id={id}
          className="sc-select__control"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="sc-select__chevron" aria-hidden="true">
          ▾
        </span>
      </div>

      {hint && (
        <p className="sc-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="sc-field__error" id={errorId}>
          <span aria-hidden="true">⚠️ </span>
          {error}
        </p>
      )}
    </div>
  );
}
